import { useState, useCallback } from 'react';
import { useDrag } from '@use-gesture/react';

/**
 * Custom hook for managing photo selection state and logic
 * @param {boolean} selectionMode - Whether selection mode is active
 * @param {Array} photos - Array of photo filenames
 * @returns {Object} Selection state and functions
 */
export const usePhotoSelection = (selectionMode, photos = []) => {
  const [selectedPhotos, setSelectedPhotos] = useState(new Set());
  const [selectAll, setSelectAll] = useState(false);
  const [lastSelectedPhoto, setLastSelectedPhoto] = useState(null);
  const [isDraggingSelection, setIsDraggingSelection] = useState(false);
  const [selectionStartState, setSelectionStartState] = useState(null);

  /**
   * Clear all selections
   */
  const clearSelections = useCallback(() => {
    setSelectedPhotos(new Set());
    setSelectAll(false);
    setIsDraggingSelection(false);
    setSelectionStartState(null);
    setLastSelectedPhoto(null);
  }, []);

  /**
   * Toggle selection of a specific photo
   * @param {string} filename - The photo filename to toggle
   */
  const togglePhotoSelection = useCallback((filename) => {
    if (!selectionMode) return;

    setSelectedPhotos(prev => {
      const newSet = new Set(prev);
      if (newSet.has(filename)) {
        newSet.delete(filename);
      } else {
        newSet.add(filename);
      }
      return newSet;
    });
    setLastSelectedPhoto(filename);
  }, [selectionMode]);

  /**
   * Toggle select all/deselect all
   */
  const toggleSelectAll = useCallback(() => {
    if (selectAll) {
      setSelectedPhotos(new Set());
      setSelectAll(false);
    } else {
      setSelectedPhotos(new Set(photos));
      setSelectAll(true);
    }
  }, [selectAll, photos]);

  /**
   * Gesture binding for drag selection
   */
  const bindSelectionGesture = useDrag(
    ({ event, first, last, movement: [mx, my], direction: [dx, dy], distance, cancel }) => {
      if (!selectionMode) return;

      if (first) {
        // Get the initial photo element
        const target = event.target.closest('[data-photo-filename]');
        if (target) {
          const filename = target.dataset.photoFilename;
          if (filename) {
            setIsDraggingSelection(true);
            setSelectionStartState(filename);
            setLastSelectedPhoto(filename);

            // Determine if we're starting with selection or deselection
            const isCurrentlySelected = selectedPhotos.has(filename);
            setSelectionStartState({ filename, action: isCurrentlySelected ? 'deselect' : 'select' });

            // Toggle the initial photo
            togglePhotoSelection(filename);
          }
        }
      } else if (isDraggingSelection) {
        // Find the photo element under the current touch point
        const element = document.elementFromPoint(event.clientX, event.clientY);
        if (element) {
          const photoContainer = element.closest('[data-photo-filename]');
          if (photoContainer) {
            const filename = photoContainer.dataset.photoFilename;
            if (filename && filename !== lastSelectedPhoto) {
              setLastSelectedPhoto(filename);

              // Apply the same action (select/deselect) as the initial photo
              if (selectionStartState && selectionStartState.action) {
                const isCurrentlySelected = selectedPhotos.has(filename);
                const shouldSelect = selectionStartState.action === 'select';

                if (shouldSelect && !isCurrentlySelected) {
                  // Add to selection
                  setSelectedPhotos(prev => new Set([...prev, filename]));
                } else if (!shouldSelect && isCurrentlySelected) {
                  // Remove from selection
                  setSelectedPhotos(prev => {
                    const newSet = new Set(prev);
                    newSet.delete(filename);
                    return newSet;
                  });
                }
              }
            }
          }
        }
      }

      if (last) {
        // End of gesture
        setIsDraggingSelection(false);
        setSelectionStartState(null);
        setLastSelectedPhoto(null);
      }
    },
    {
      // Configuration for better touch handling
      filterTaps: true,
      threshold: 5, // Minimum distance to start drag
      preventDefault: true,
      eventOptions: { passive: false }
    }
  );

  return {
    // State
    selectedPhotos,
    selectAll,
    lastSelectedPhoto,
    isDraggingSelection,
    selectionStartState,

    // Functions
    clearSelections,
    togglePhotoSelection,
    toggleSelectAll,
    bindSelectionGesture
  };
};
